import { useContext, useEffect, useRef, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ChecklistType } from '../../../../service/Checklists';
import AttToken from '../../../../helpers/attToken';
import { toast } from 'react-toastify';
import Clients, { ClientType } from '../../../../service/Clients';
import {
  Accordion,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ConjuntoType, PlaceType, UnidadeType } from '../../../../service/Places';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserContext } from '../../../../context/UserContext';
import { trackEventMatomo } from '../../../../helpers/matomo';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import QueryFn from '../../../../helpers/QueryFn';

export type linkType = {
  index: number,
  name: string,
  value: string,
  blocked?: boolean
};

export type chamadosAssociacoesType = {
  cliente: string | null,
  local: string | null,
  conjunto: string | null,
  unidade: string | null
};

const clienteService = new Clients();
const queryFn = new QueryFn();

export default function FillingConfig({
  formId,
  formWeb,
  form
}: {
  formId: string,
  formWeb: boolean,
  form: ChecklistType
}) {
  const { t } = useTranslation('translation');

  const { hierarchyLabel, userData } = useContext(UserContext);

  const [chamadoAssociacoes, setChamadoAssociacoes] = useState<chamadosAssociacoesType | null>(null);
  const [openTicketAfter, setOpenTicketAfter] = useState(form.abreChamado ? form.abreChamado.apos : false);

  const [link, setLink] = useState<linkType[] | null>(form.link || null);

  const linkRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (link && link.length > 0) {
      form.link = link?.map((e) => (
        e
      ));
    }
  }, [link]);

  const getHierarquia = async () => {
    const token = await AttToken();
    if (token) {
      const result = await clienteService.getAllHierarchy(token);
      return result;
    }
  };

  const result = queryFn.useFetch('HIERARQUIA', getHierarquia);

  const handleTicketAfter = () => {
    setOpenTicketAfter(!openTicketAfter);
    if (form.abreChamado) {
      form.abreChamado.apos = !openTicketAfter;
    } else form.abreChamado = {
      apos: !openTicketAfter,
      durante: false,
    };
    trackEventMatomo('modal config form', 'click', 'input', 'abertura de chamados');
  };

  const findName = (key: 'clientes' | 'locais' | 'conjuntos', id: string): string => {
    const selected = result.data
      ? result.data[key]?.find((el: ConjuntoType | ClientType | PlaceType) => el.id === id)
      : { nome: `${t('Filling.fillSubitens.nameNotFound')}`, id: 'A' };
    return selected ? selected.nome : `${t('Filling.fillSubitens.nameNotFound')}`;
  };

  const getValuesForLink = () => {
    trackEventMatomo('modal config form', 'click', 'button', 'gera link');
    link && setLink(link?.filter(link => !['cliente', 'local', 'conjunto', 'unidade']?.includes(link?.name)));
    Object?.keys(chamadoAssociacoes || {})?.forEach((key, i, a) => {
      const val = chamadoAssociacoes ? chamadoAssociacoes[key as keyof typeof chamadoAssociacoes] : null;
      setLink(prev => prev && prev[0]?.name
        ? [...prev, { index: prev?.length, name: key, value: val || 'all', blocked: true }]
        : [{ index: 0, name: key, value: val || 'all', blocked: true }]);
    });
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={1}
    >
      {formWeb &&
        <>
          <Stack
            width={'100%'}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={0}
          >
            <TextField
              multiline
              inputRef={linkRef}
              variant='outlined'
              fullWidth
              disabled
              label={t('checklists.subitemModal.preenchimento.Link')}
              value={link?.map((e, i) => {
                if (e.value && e.name) {
                  if (i === 0) return `${process.env.REACT_APP_AXYMA_FRONT_URL}preenchedor/${formId}?${e.name}=${e.value}`;
                  else return `&${e.name}=${e.value}`;
                } else return '';
              })?.join('') || `${process.env.REACT_APP_AXYMA_FRONT_URL}/preenchedor/${formId}`}
            />
            <Tooltip title={` ${t('checklists.subitemModal.preenchimento.copiar')}`}>
              <ContentCopyIcon
                sx={{ ':hover': { cursor: 'pointer' } }}
                color='success'
                onClick={async () => {
                  trackEventMatomo('modal config form', 'click', 'button', 'copia link');
                  await navigator.clipboard.writeText(linkRef.current?.value as string);
                  alert(`${t('checklists.formModal.linkCopiedToClipboard')}`);
                }}
                fontSize='large' />
            </Tooltip>
          </Stack>
          {link?.map((e, i) => (
            <Stack
              width={'100%'}
              direction="row"
              justifyContent='flex-start'
              alignItems="center"
              spacing={0}
            >
              <TextField
                label={t('name')}
                variant='outlined'
                onClick={() => trackEventMatomo('modal config form', 'click', 'select', 'seleciona hierarquia')}
                disabled={e.blocked}
                value={link[i].name}
                onChange={(e) => {
                  if (['conjunto', 'local', 'cliente', 'unidade']?.includes(e.target.value)) {
                    toast.warn(`${t('toast.forbiddenWord')}`);
                  } else {
                    const newState = link.map((item, index) => {
                      if (index === i) {
                        item.name = e.target.value;
                      }
                      return item;
                    });
                    setLink(newState);
                  }
                }}
              />
              <TextField
                fullWidth
                variant='outlined'
                label={t('checklists.subitemModal.preenchimento.valor')}
                disabled={link[i].name === '' || e.blocked}
                value={link[i].value}
                onChange={(e) => {
                  const newState = link.map((item, index) => {
                    if (index === i) {
                      item.value = e.target.value;
                    }
                    return item;
                  });
                  setLink(newState);
                }}
              />
              {i === 0 ?
                <AddCircleIcon
                  sx={{ ':hover': { cursor: 'pointer' } }}
                  fontSize='large'
                  color='success'
                  onClick={() => {
                    trackEventMatomo('modal config form', 'click', 'button', 'adiciona link');
                    setLink([...link, { name: '', value: '', index: link.length }]);
                  }}
                />
                :
                <RemoveCircleIcon
                  fontSize='large'
                  color='error'
                  onClick={() => {
                    trackEventMatomo('modal config form', 'click', 'button', 'deleta link');
                    setLink(link.filter((el) => el.index !== e.index));
                  }}
                />
              }
            </Stack>
          ))}
          <FormControlLabel sx={{ width: '100%', textAlign: 'left' }}
            control={<Checkbox
              checked={openTicketAfter}
              onChange={handleTicketAfter}
            />} label={t('checklists.allowActionPlanOpening')} />
          {userData.migrado && (!chamadoAssociacoes || !chamadoAssociacoes.cliente) && (
            <span className='tag is-warning'>{t('checklists.formModal.pleaseAssociateHierarchyForCallOpening')}</span>
          )}
          {userData.migrado && (
            <Accordion style={{ width: '100%' }}>
              <AccordionSummary
                onClick={() => trackEventMatomo('modal config form', 'click', 'acordeon', 'abre/fecha')} expandIcon={<ExpandMoreIcon />}>
                <Typography variant='subtitle1'>{t('checklists.formModal.hierarchyConfigurationForCallOpening')}</Typography>
              </AccordionSummary>
              {result.isLoading ? (<Skeleton variant='rounded' width={'100%'} height={40} />) : (
                <Stack
                  key={chamadoAssociacoes?.cliente}
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  sx={{ padding: '10px' }}
                >
                  {chamadoAssociacoes && chamadoAssociacoes.cliente && (
                    <Typography variant='body2' fontWeight='bold' sx={{ backgroundColor: '#FEE08A', padding: '1.5px', borderRadius: '5px' }}>
                      {
                        chamadoAssociacoes.cliente && !chamadoAssociacoes.local ? (
                          `${t('checklists.formModal.teamsServingAll')} ${hierarchyLabel.conjunto} ${t('checklists.formModal.ofAll')} ${hierarchyLabel.local} ${t('checklists.formModal.of')} ${hierarchyLabel.cliente} "${findName('clientes', chamadoAssociacoes.cliente)}" ${t('checklists.formModal.willReceiveCalls')}`
                        ) : chamadoAssociacoes.cliente && chamadoAssociacoes.local && !chamadoAssociacoes.conjunto ? (
                          `${t('checklists.formModal.teamsServingAll')} ${hierarchyLabel.conjunto} ${t('checklists.formModal.of')} ${hierarchyLabel.local} "${findName('locais', chamadoAssociacoes.local)}" ${t('checklists.formModal.willReceiveCalls')}`
                        ) : chamadoAssociacoes.cliente && chamadoAssociacoes.local && chamadoAssociacoes.conjunto ? (
                          `${t('checklists.formModal.teamsServingSpecific')} ${hierarchyLabel.conjunto} "${findName('conjuntos', chamadoAssociacoes.conjunto)}" ${t('checklists.formModal.willReceiveCalls')}`
                        ) : ('')
                      }
                    </Typography>
                  )}
                  <Autocomplete
                    value={chamadoAssociacoes?.cliente ? {
                      value: chamadoAssociacoes?.cliente,
                      label: result.data?.clientes?.find((cliente: ClientType) => cliente.id === chamadoAssociacoes?.cliente)?.nome,
                    } : undefined}
                    onClick={() => trackEventMatomo('modal config form', 'click', 'select', 'seleciona cliente')}
                    disableClearable
                    fullWidth
                    noOptionsText={`Não existem clientes nesta empresa...`}
                    renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel.cliente} />}
                    options={
                      result.data?.clientes
                        ?.sort((a: ClientType, b: ClientType) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: ClientType) => ({
                          label: ele.nome,
                          value: ele.id,
                        })) || []}
                    onChange={(event: any, newValue: { label: string, value: string } | null) => {
                      const clienteId = newValue?.value;
                      setChamadoAssociacoes({ cliente: clienteId as string, local: null, conjunto: null, unidade: null });
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value}>
                        <span>{option?.label}</span>
                      </li>)}
                  />
                  <Autocomplete
                    disabled={!chamadoAssociacoes?.cliente}
                    onClick={() => trackEventMatomo('modal config form', 'click', 'select', 'seleciona local')}
                    disableClearable
                    noOptionsText={'Sem locais associados...'}
                    fullWidth
                    renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel?.local} />}
                    options={
                      result.data?.locais.filter((cl: PlaceType) => cl.cliente === chamadoAssociacoes?.cliente)
                        ?.sort((a: ClientType, b: ClientType) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: ClientType) => ({
                          label: ele.nome,
                          value: ele.id,
                        })) || []}
                    onChange={(event: any, newValue: { label: string, value: string } | null) => {
                      const id = newValue?.value;
                      setChamadoAssociacoes(prev => prev && { ...prev, local: id as string, conjunto: null, unidade: null });
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value}>
                        <span>{option?.label}</span>
                      </li>)}
                  />
                  <Autocomplete
                    disabled={!chamadoAssociacoes?.local}
                    onClick={() => trackEventMatomo('modal config form', 'click', 'select', 'seleciona conjunto')}
                    disableClearable
                    noOptionsText={'Sem conjuntos associados...'}
                    fullWidth
                    renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel?.conjunto} />}
                    options={
                      result.data?.conjuntos?.filter((cnj: ConjuntoType) => cnj.local === chamadoAssociacoes?.local)
                        ?.sort((a: ClientType, b: ClientType) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: ClientType) => ({
                          label: ele.nome,
                          value: ele.id,
                        })) || []}
                    onChange={(event: any, newValue: { label: string, value: string } | null) => {
                      const id = newValue?.value;
                      setChamadoAssociacoes(prev => prev && { ...prev, conjunto: id as string, unidade: null });
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value}>
                        <span>{option?.label}</span>
                      </li>)}
                  />
                  <Autocomplete
                    disabled={!chamadoAssociacoes?.conjunto}
                    onClick={() => trackEventMatomo('modal config form', 'click', 'select', 'seleciona unidade')}
                    disableClearable
                    noOptionsText={'Sem unidades associadas...'}
                    fullWidth
                    renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel?.unidade} />}
                    options={
                      result.data?.unidades?.filter((uni: UnidadeType) => uni.conjunto === chamadoAssociacoes?.conjunto)
                        ?.sort((a: ClientType, b: ClientType) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: ClientType) => ({
                          label: ele.nome,
                          value: ele.id,
                        })) || []}
                    onChange={(event: any, newValue: { label: string, value: string } | null) => {
                      const id = newValue?.value;
                      setChamadoAssociacoes(prev => prev && { ...prev, unidade: id as string });
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value}>
                        <span>{option?.label}</span>
                      </li>)}
                  />
                  <Stack
                    sx={{ width: '100%' }}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      endIcon={<TaskAltIcon />}
                      className={`button is-small  ${(!chamadoAssociacoes || !chamadoAssociacoes.cliente) ? 'is-warning' : 'is-success'}`}
                      disabled={(!chamadoAssociacoes || !chamadoAssociacoes.cliente)}
                      onClick={() => getValuesForLink()}
                    >
                      {(!chamadoAssociacoes || !chamadoAssociacoes.cliente)
                        ? `${t('checklists.formModal.selectAtLeastOne')} ${hierarchyLabel.cliente} ${t('checklists.formModal.toAssociate')}`
                        : `${t('checklists.formModal.applyValuesToLink')}`}
                    </Button>
                    <Button
                      endIcon={<DeleteIcon />}
                      className='button is-danger is-small'
                      onClick={() => {
                        trackEventMatomo('modal config form', 'click', 'button', 'limpa link');
                        link && setLink(link.filter(link => !['conjunto', 'local', 'cliente', 'unidade']?.includes(link?.name)));
                        setChamadoAssociacoes(null);
                        form.link = [];
                      }}
                    >
                      {t('checklists.formModal.clearLinkValues')}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Accordion>
          )}
        </>}
    </Stack>
  );
}